import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import FormContainer from '../../components/FormContainer';
import { toast } from 'react-toastify';
import {
  useGetHouseDetailsQuery,
  useUpdateHouseMutation,
  useUploadHouseImagesMutation,
} from '../../slices/housesApiSlice';

const HouseStaffEditScreen = () => {
  const { id: houseId } = useParams();

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  const [parking, setParking] = useState(false);
  const [furnished, setFurnished] = useState(false);
  const [address, setAddress] = useState('');
  const [offer, setOffer] = useState(false);
  const [regularPrice, setRegularPrice] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [images, setImages] = useState([]);
  const [size, setSize] = useState('');
  const [agent, setAgent] = useState('');

  const {
    data: house,
    isLoading,
    refetch,
    error,
  } = useGetHouseDetailsQuery(houseId);

  const [updateHouse, { isLoading: loadingUpdate }] = useUpdateHouseMutation();
  const [uploadHouseImages, { isLoading: loadingUpload }] =
    useUploadHouseImagesMutation();

  const navigate = useNavigate();

  useEffect(() => {
    if (house) {
      setName(house.name);
      setType(house.type);
      setBedrooms(house.bedrooms);
      setBathrooms(house.bathrooms);
      setParking(house.parking);
      setFurnished(house.furnished);
      setAddress(house.address);
      setOffer(house.offer);
      setRegularPrice(house.regularPrice);
      setDiscountedPrice(house.discountedPrice);
      setLatitude(house.latitude);
      setLongitude(house.longitude);
      setImages(house.images || []);
      setSize(house.size || '');
      setAgent(house.agent || '');
    }
  }, [house]);

  const validateFiles = (files) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    for (let file of files) {
      if (!validTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds limit: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const uploadFileHandler = async (e) => {
    const files = e.target.files;
    if (!validateFiles(files)) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    try {
      const res = await uploadHouseImages(formData).unwrap();
      toast.success(res.message);
      setImages([...images, ...res.images]); // Add new images to existing array
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await updateHouse({
        houseId,
        name,
        type,
        bedrooms,
        bathrooms,
        parking,
        furnished,
        address,
        offer,
        regularPrice,
        discountedPrice,
        latitude,
        longitude,
        images,
        size,
        agent,

      }).unwrap();
      toast.success('House updated successfully');
      refetch();
      navigate('/staffhouselist');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <Link to="/staffhouselist" className="btn btn-light my-3">
        Go Back
      </Link>
      <FormContainer>
        <h1>Edit House</h1>
        {loadingUpdate && <Loader />}
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error.data.message}</Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group controlId="name">
            <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter house name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="type">
              <Form.Label>Type</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter house type (e.g., apartment, villa)"
                value={type}
                onChange={(e) => setType(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="bedrooms">
              <Form.Label>Bedrooms</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter number of bedrooms"
                value={bedrooms}
                onChange={(e) => setBedrooms(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="bathrooms">
              <Form.Label>Bathrooms</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter number of bathrooms"
                value={bathrooms}
                onChange={(e) => setBathrooms(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="parking">
              <Form.Check
                type="checkbox"
                label="Parking Available"
                checked={parking}
                onChange={(e) => setParking(e.target.checked)}
              ></Form.Check>
            </Form.Group>

            <Form.Group controlId="furnished">
              <Form.Check
                type="checkbox"
                label="Furnished"
                checked={furnished}
                onChange={(e) => setFurnished(e.target.checked)}
              ></Form.Check>
            </Form.Group>

            <Form.Group controlId="address">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter house address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="offer">
              <Form.Check
                type="checkbox"
                label="Special Offer"
                checked={offer}
                onChange={(e) => setOffer(e.target.checked)}
              ></Form.Check>
            </Form.Group>

            <Form.Group controlId="regularPrice">
              <Form.Label>Regular Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter regular price"
                value={regularPrice}
                onChange={(e) => setRegularPrice(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="discountedPrice">
              <Form.Label>Discounted Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter discounted price (if applicable)"
                value={discountedPrice}
                onChange={(e) => setDiscountedPrice(e.target.value)}
                disabled={!offer}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="latitude">
              <Form.Label>Latitude</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter latitude"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId="longitude">
              <Form.Label>Longitude</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter longitude"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              ></Form.Control>
            </Form.Group>
            <Form.Group controlId="size">
                <Form.Label>Size</Form.Label>
                <Form.Control
                 type="text"
                 placeholder="Enter house size or plot measurement"
                 value={size}
                onChange={(e) => setSize(e.target.value)}
                ></Form.Control>
               </Form.Group>
               <Form.Group controlId="agent">
                <Form.Label>Agent</Form.Label>
                <Form.Control
                 type="text"
                 placeholder="Enter agent name"
                 value={agent}
                onChange={(e) => setAgent(e.target.value)}
                ></Form.Control>
               </Form.Group>
            {/* Form Fields for House Details */}
            <Form.Group controlId="images">
              <Form.Label>Images</Form.Label>
              <div
                style={{
                  border: '2px dashed #ddd',
                  padding: '1rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (validateFiles(e.dataTransfer.files)) {
                    uploadFileHandler({ target: { files: e.dataTransfer.files } });
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                Drag and drop files here, or click to upload
              </div>
              <Form.Control
                type="file"
                multiple
                onChange={uploadFileHandler}
                style={{ marginTop: '1rem' }}
              />
              {loadingUpload && <Loader />}
              <div className="mt-2">
                {images.map((img, index) => (
                  <div key={index} style={{ display: 'inline-block', position: 'relative' }}>
                    <img
                      src={img}
                      alt={`preview-${index}`}
                      style={{ width: '100px', marginRight: '10px' }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        borderRadius: '50%',
                      }}
                      onClick={() => removeImageHandler(index)}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
              </div>
            </Form.Group>
            <Button type="submit" variant="primary" style={{ marginTop: '1rem' }}>
              Update
            </Button>
          </Form>
        )}
      </FormContainer>
    </>
  );
};

export default HouseStaffEditScreen;